/* T&M (Time & Material) engine — matches the SERVPRO 2026 TM Calculator Excel.
 *
 * Line-item driven. No coefficients. Estimator fills the 5 sections (A-E) and
 * we compute totals exactly as the spreadsheet does, including markup logic.
 *
 * Markup logic (per the Excel):
 *   B Consumables = pct * A_total (default 5%, options 5/10/15/20)
 *   D Subcontractors line subtotal = base × (1 + markup)        (default 20%)
 *   E Miscellaneous line subtotal = base × 1.20                 (10+10% = 20%)
 *   A Labour line = qty × ((reg_hrs × bill_rate) + (ot_hrs × ot_rate)) × days
 *   C Equipment line = qty × days × daily_rate                  (no markup baked in)
 */

const n = (x) => (Number(x) || 0);

export function labourLineSubtotal(line) {
    const qty = n(line.qty);
    const hrs = n(line.hours);
    const otHrs = n(line.otHours);
    const bill = n(line.billingRate);
    const ot = n(line.otRate);
    const days = n(line.days);
    return qty * ((hrs * bill) + (otHrs * ot)) * days;
}

export function labourTotal(lines = []) {
    return lines.reduce((s, l) => s + labourLineSubtotal(l), 0);
}

export function consumablesTotal(labourTot, pct = 0.05) {
    return labourTot * n(pct);
}

export function equipmentLineSubtotal(line) {
    return n(line.qty) * n(line.days) * n(line.dailyRate);
}

export function equipmentTotal(lines = []) {
    return lines.reduce((s, l) => s + equipmentLineSubtotal(l), 0);
}

export function subLineSubtotal(line) {
    const base = n(line.qty) * n(line.dailyRate) * n(line.days || 1);
    const mk = n(line.markupPct ?? 0.20);
    return base * (1 + mk);
}

export function subTotal(lines = []) {
    return lines.reduce((s, l) => s + subLineSubtotal(l), 0);
}

export function miscLineSubtotal(line) {
    // Base = qty × rate × (days or 1)
    const base = n(line.qty) * n(line.rate) * (line.days != null ? n(line.days) : 1);
    // 10+10% markup = 20% (per Excel which uses 1.20 multiplier)
    return base * 1.20;
}

export function miscTotal(lines = []) {
    return lines.reduce((s, l) => s + miscLineSubtotal(l), 0);
}

export function computeTM(tm) {
    const labour = labourTotal(tm.labour);
    const consumables = consumablesTotal(labour, tm.consumablesPct ?? 0.05);
    const equipment = equipmentTotal(tm.equipment);
    const subs = subTotal(tm.subcontractors);
    const misc = miscTotal(tm.miscellaneous);
    return {
        labour, consumables, equipment, subs, misc,
        grandTotal: labour + consumables + equipment + subs + misc,
    };
}

export function buildEmptyLabourFromRateCard(scheduleA = [], usePrime = false) {
    // Build a labour table with all roles from the rate schedule, qty=0.
    return scheduleA.map((r) => ({
        role: r.role,
        qty: 0,
        billingRate: usePrime ? n(r.prime) : n(r.standard),
        hours: 0,
        otRate: (usePrime ? n(r.prime) : n(r.standard)) * (n(r.ot_mult) || 1.5),
        otHours: 0,
        days: 0,
    }));
}

export function buildEmptyTM() {
    return {
        labour: [],
        consumablesPct: 0.05,
        equipment: [],
        subcontractors: [],
        miscellaneous: [
            { kind: "Airfare", unitLabel: "Round Trip", qty: 0, rate: 0 },
            { kind: "Lodging", unitLabel: "# of Rooms", qty: 0, rate: 0, days: 0 },
            { kind: "Per Diem", unitLabel: "# of People", qty: 0, rate: 60, days: 0 },
            { kind: "Mobilization", unitLabel: "# of People", qty: 0, rate: 0, days: 0 },
            { kind: "Fuel", unitLabel: "# of Gallons", qty: 0, rate: 0, days: 1 },
            { kind: "Misc.", unitLabel: "Daily Rate", qty: 0, rate: 0, days: 0 },
        ],
    };
}

export function staffingSummary(labourLines = []) {
    const active = labourLines.filter((l) => n(l.qty) > 0);
    const crew = active.reduce((s, l) => s + n(l.qty), 0);
    const regHrs = active.reduce((s, l) => s + n(l.qty) * n(l.hours) * n(l.days), 0);
    const otHrs = active.reduce((s, l) => s + n(l.qty) * n(l.otHours) * n(l.days), 0);
    const maxDays = active.reduce((m, l) => Math.max(m, n(l.days)), 0);
    return {
        crew,
        regHours: regHrs,
        otHours: otHrs,
        totalHours: regHrs + otHrs,
        days: maxDays,
        roles: active,
    };
}


/* ---------------- Commercial Cleaning (Schedule F) ----------------
 * Standalone cleaning is flat unit-rate pricing (qty × rate), NOT the loss-type
 * coefficient ROM. Markup defaults to 0% — Schedule F rates already carry margin.
 * markupPct is an optional per-line override, never hardcoded.
 */
export function flatLineSubtotal(line) {
    const base = n(line.qty) * n(line.rate);
    const mk = n(line.markupPct ?? 0);
    return base * (1 + mk);
}

export function flatTotal(lines = []) {
    return lines.reduce((s, l) => s + flatLineSubtotal(l), 0);
}

export function buildEmptyCleaning() {
    return {
        floorSqft: "", wallSqft: "", ceilingSqft: "",
        facilityType: "",
        tasks: {
            hepa_floor_ceiling: true,  // HEPA Vacuuming, Detailed (floor + ceiling)
            hepa_walls: true,          // HEPA Vacuuming, Light (walls)
            clean_floor: true,
            clean_walls: true,
            clean_ceiling: true,
        },
        antimicrobial: false,
        atp: false, atpQty: 1,
        openings: [],     // [{ item, qty }] doors / windows / debris hauls
        labour: [],       // [{ role, qty, hours }]
        equipment: [],    // [{ item, qty, days }]
        techCount: 0, days: 0, includePPE: false,
    };
}

function scheduleFRate(schedule) {
    const map = {};
    (schedule?.schedule_f || []).forEach((r) => { map[r.item] = Number(r.rate) || 0; });
    return map;
}

/* Build the full editable line list + total for a cleaning quote.
 * Surface / antimicrobial / ATP / PPE rates come live from Schedule F (centrally
 * editable); openings/labour/equipment carry their own per-line rate. */
export function computeCleaning(cleaning, schedule) {
    const c = cleaning || buildEmptyCleaning();
    const F = scheduleFRate(schedule);
    const rate = (item) => Number(F[item] ?? 0);
    const floor = n(c.floorSqft), wall = n(c.wallSqft), ceil = n(c.ceilingSqft);
    const lines = [];
    const push = (group, item, qty, unit, r, markupPct = 0) => {
        if (!(qty > 0)) return;
        const rr = r != null ? r : rate(item);
        lines.push({ group, item, qty, unit, rate: rr, markupPct, subtotal: qty * rr * (1 + markupPct) });
    };

    const t = c.tasks || {};
    if (t.hepa_floor_ceiling) {
        push("Surface prep", "HEPA Vacuuming, Detailed", floor, "sf · floor");
        push("Surface prep", "HEPA Vacuuming, Detailed", ceil, "sf · ceiling");
    }
    if (t.hepa_walls) push("Surface prep", "HEPA Vacuuming, Light", wall, "sf · walls");
    if (t.clean_floor) push("Cleaning", "Clean floor", floor, "sf");
    if (t.clean_walls) push("Cleaning", "Clean walls", wall, "sf");
    if (t.clean_ceiling) push("Cleaning", "Clean ceiling", ceil, "sf");

    if (c.antimicrobial) {
        push("Sanitization", "Apply anti-microbial agent", floor, "sf · floor");
        push("Sanitization", "Apply anti-microbial agent", wall, "sf · walls");
        push("Sanitization", "Apply anti-microbial agent", ceil, "sf · ceiling");
    }
    if (c.atp) push("Verification", "On-site ATP testing", n(c.atpQty), "tests");

    (c.openings || []).forEach((o) => push("Openings", o.item, n(o.qty), "ea", rate(o.item)));

    (c.labour || []).forEach((l) => {
        const qty = n(l.qty) * n(l.hours);
        const r = rate(l.role);
        if (qty > 0) lines.push({ group: "Labour", item: `${l.role} — ${n(l.qty)} × ${n(l.hours)}h`, qty, unit: "hrs", rate: r, markupPct: 0, subtotal: qty * r });
    });

    (c.equipment || []).forEach((e) => {
        const meta = (schedule?.schedule_f || []).find((x) => x.item === e.item) || {};
        const isDaily = !!meta.daily;
        const qty = isDaily ? n(e.qty) * n(e.days || 1) : n(e.qty);
        const r = rate(e.item);
        if (qty > 0) lines.push({ group: "Equipment", item: e.item, qty, unit: isDaily ? "unit·day" : "ea", rate: r, markupPct: 0, subtotal: qty * r });
    });

    if (c.includePPE) {
        push("Consumables", "PPE consumables (N95 / goggles / gloves)", n(c.techCount) * n(c.days), "tech·day");
    }

    const grand_total = lines.reduce((s, l) => s + l.subtotal, 0);
    const groups = {};
    lines.forEach((l) => { groups[l.group] = (groups[l.group] || 0) + l.subtotal; });
    return { mode: "cleaning", lines, groups, grand_total };
}
